import type { FieldValues, Path } from 'react-hook-form';
import type { InspectorDTO } from 'src/types/dto';
import type { AddressInfoType } from 'src/types/inspector';
import type {
  MapProviderType,
  LatLngLiteral,
  MapMarker as IMapMarker,
  MapOptions,
} from 'src/types/map-provider';

import React, { useRef, useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';

import { useMapProvider } from 'src/hooks/use-map-provider';
import { useMapInstance } from 'src/hooks/use-map-instance';
import { useMapMarkers } from 'src/hooks/use-map-markers';
import { useMapFormSync } from 'src/hooks/use-map-form-sync';
import { useEnvMapProvider, useEnvMapApiKeys } from 'src/hooks/use-env-map-provider';

import { MapError } from './components/map-error';
import { MapErrorState } from './components/map-error-state';
import { MapLoadingState } from './components/map-loading-state';
import { MapSearchOverlay } from './components/map-search-overlay';
import { MapProviderIndicator } from './components/map-provider-indicator';
import { MapInspectorInfoWindow } from './components/map-inspector-info-window';
import { MAIN_CONTAINER_STYLES, MAP_ELEMENT_STYLES } from './styles/universal-location-map.styles';

export interface UniversalLocationMapProps<T extends FieldValues> {
  methods?: any; 
  mapProviderType?: MapProviderType | 'auto';
  apiKeys?: Partial<Record<MapProviderType, string>>;
  setAddressInfo?: (info: AddressInfoType) => void;
  tooltipText?: string;
  placeholderText?: string;
  onChange?: (value: string) => void;
  viewOnly?: boolean;
  defaultLocation?: string;
  markers?: IMapMarker[];
  onMarkerDragEnd?: (index: number, position: LatLngLiteral, address?: string, addressInfo?: AddressInfoType) => void;
  onMarkerClick?: (index: number) => void;
  highlightedMarkerId?: number;
  selectedIds?: number[];
  selectedMarker?: InspectorDTO | null;
  onMarkerSelect?: (inspector: InspectorDTO | null) => void;
  onInspectorSelect?: (inspectorId: number) => void;
  hideSearchInput?: boolean;
  defaultZoom?: number;
  height?: string | number;
  className?: string;
  style?: React.CSSProperties;
}

export function UniversalLocationMap<T extends FieldValues>({
  methods,
  mapProviderType,
  apiKeys,
  setAddressInfo,
  tooltipText,
  placeholderText,
  onChange,
  viewOnly = false,
  defaultLocation,
  markers = [],
  onMarkerDragEnd,
  onMarkerClick,
  highlightedMarkerId,
  selectedIds,
  selectedMarker,
  onMarkerSelect,
  onInspectorSelect,
  hideSearchInput = false,
  defaultZoom,
  height = '400px',
  className,
  style,
}: UniversalLocationMapProps<T>) {
  // Translation hook - using both 'maps' and 'common' namespaces
  const { t } = useTranslation(['maps', 'common']);

  // Generate unique ID for map container
  const mapContainerId = React.useId();

  // Ref for map container element
  const mapElementRef = useRef<HTMLDivElement>(null);

  // Local markers state - initialized from props or form location
  const [localMarkers, setLocalMarkers] = useState<IMapMarker[]>(() => {
    // Initialize from props if provided
    if (markers && markers.length > 0) {
      return markers;
    }

    // Otherwise create default marker from form location
    const location = methods?.getValues('location' as Path<T>) || defaultLocation;
    if (location) {
      const [lat, lng] = location.split(',').map(Number);
      if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
        return [{
          position: { lat, lng },
          draggable: !viewOnly,
          title: t('dragToUpdate', { ns: 'maps' }),
        }];
      }
    }

    // Fallback to default location (Berlin)
    return [{
      position: { lat: 52.520008, lng: 13.404954 },
      draggable: !viewOnly,
      title: t('dragToUpdate', { ns: 'maps' }),
    }];
  });

  // Sync props markers with local state
  useEffect(() => {
    if (markers && markers.length > 0) {
      setLocalMarkers(markers);
    }
  }, [markers]);

  // Get map provider configurations from environment variables
  const envMapProvider = useEnvMapProvider();
  const envApiKeys = useEnvMapApiKeys();

  // Get map provider from props OR from configured environment variable
  const selectedProviderType = mapProviderType ?? envMapProvider;
  
  const isUniversalMapsEnabled = process.env.NEXT_PUBLIC_ENABLE_UNIVERSAL_MAPS === 'true';
  
  // First, get API keys from environment variables
  // Then, get API keys from props
  // Then, return the combined API keys (props take precedence over environment variables)
  const effectiveApiKeys = {
    ...envApiKeys,
    ...apiKeys, 
  };
  
  // Determine if there is a configuration error
  const configError = (() => {
    if (!isUniversalMapsEnabled || (!effectiveApiKeys.google && !effectiveApiKeys.baidu)) {
      return 'setup';
    }
    if (selectedProviderType === 'google' && !effectiveApiKeys.google) {
      return 'google-key-missing';
    }
    if (selectedProviderType === 'baidu' && !effectiveApiKeys.baidu) {
      return 'baidu-key-missing';
    }
    return null;
  })();

  const initialLocation = useMemo<LatLngLiteral>(() => {
    const location = methods?.getValues('location' as Path<T>) || defaultLocation;
    if (location && typeof location === 'string') {
      const [lat, lng] = location.split(',').map(Number);
      if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
        return { lat, lng };
      }
    }
    return { lat: 52.520008, lng: 13.404954 }; 
  }, [methods, defaultLocation]);

  const mapOptions: MapOptions = useMemo(() => ({
    center: initialLocation,
    zoom: defaultZoom ?? (viewOnly ? 15 : 17),
    gestureHandling: 'cooperative',
    disableDefaultUI: viewOnly,
    controls: {
      fullscreen: false,
      streetView: false,
      mapType: false,
      scale: false,
      zoom: !viewOnly,
    },
    styles: [
      { featureType: 'poi', stylers: [{ visibility: 'off' }] },
      { featureType: 'poi.park', stylers: [{ visibility: 'off' }] },
      { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
      { featureType: 'poi.attraction', stylers: [{ visibility: 'off' }] },
      { featureType: 'poi.place_of_worship', stylers: [{ visibility: 'off' }] },
      { featureType: 'poi.sports_complex', stylers: [{ visibility: 'off' }] },
      { featureType: 'transit', stylers: [{ visibility: 'off' }] },
    ],
  }), [initialLocation, viewOnly]);

  // Use new specialized hooks
  const {
    provider,
    providerType: currentProviderType,
    isLoading,
    error,
    reverseGeocode,
  } = useMapProvider({
    providerType: selectedProviderType,
    apiKeys: effectiveApiKeys,
    autoLoad: true,
  });

  const { mapInstance } = useMapInstance({
    provider,
    mapOptions,
    containerElement: mapElementRef.current,
    autoCreate: true,
  });

  // Use form sync hook for managing form state and callbacks
  const {
    currentAddress,
    searchValue,
    setSearchValue,
    handlePlaceSelect,
    handleMarkerDragEnd,
  } = useMapFormSync({
    provider,
    mapInstance,
    reverseGeocode,
    methods,
    setAddressInfo,
    onChange,
    onMarkerDragEnd,
    onMarkersUpdate: setLocalMarkers,
    initialAddress: methods?.getValues('address' as Path<T>) || '',
    viewOnly,
    t,
  });

  // Use map markers hook - handles all marker logic automatically
  useMapMarkers({
    provider,
    mapInstance,
    markers: localMarkers,
    viewOnly,
    initialLocation,
    highlightedMarkerId,
    selectedIds,
    onMarkerClick,
    onMarkerDragEnd: handleMarkerDragEnd,
    t,
  });


  // Computed conditional rendering flags
  const shouldShowSearchInput = !viewOnly && !hideSearchInput && !!provider;
  const shouldShowProviderIndicator = process.env.NODE_ENV === 'development' && !!currentProviderType;
  const shouldShowInfoWindow = !!selectedMarker && !!selectedMarker.location;

  // Computed values for selected marker info window
  const isInspectorSelected = selectedIds?.includes(selectedMarker?.id ?? -1);
  const selectButtonText = isInspectorSelected ? t('common.selected') : t('common.select');

  // Computed provider text
  const providerText = currentProviderType === 'google'
    ? t('providers.google', { ns: 'maps' })
    : t('providers.baidu', { ns: 'maps' });

  // Render error components based on configuration errors
  if (configError) {
    return (
      <MapError
        configError={configError}
        height={height}
        isUniversalMapsEnabled={isUniversalMapsEnabled}
        hasGoogleKey={!!effectiveApiKeys.google}
        hasBaiduKey={!!effectiveApiKeys.baidu}
        errorMessage={t('errors.providerNotSupported', { ns: 'maps' })}
      />
    );
  }

  if (error) {
    return (
      <MapErrorState
        height={height}
        errorMessage={error.message}
        providerType={currentProviderType ?? undefined}
      />
    );
  }

  if (isLoading) {
    return (
      <MapLoadingState
        height={height}
        className={className}
        style={style}
        loadingText={t('loading', { ns: 'maps' })}
      />
    );
  }

  const searchInputPlaceholder = placeholderText || t('search.placeholder', { ns: 'maps' });
  const mainContainerStylesWithHeight = { ...MAIN_CONTAINER_STYLES, height, className, ...style };

  return (
    <Box sx={mainContainerStylesWithHeight}>
      {/* Map container */}
      <Box ref={mapElementRef} id={mapContainerId} sx={MAP_ELEMENT_STYLES} />

      {/* Search input */}
      {shouldShowSearchInput && (
        <MapSearchOverlay
          provider={provider}
          value={searchValue}
          placeholder={searchInputPlaceholder}
          onChange={setSearchValue}
          onPlaceSelect={handlePlaceSelect}
          tooltipText={tooltipText}
        />
      )}

      {/* Provider indicator */}
      {shouldShowProviderIndicator && <MapProviderIndicator providerText={providerText} />}

      {/* Inspector info window */}
      {shouldShowInfoWindow && onInspectorSelect && (
        <MapInspectorInfoWindow
          selectedMarker={selectedMarker}
          onClose={() => onMarkerSelect?.(null)}
          onSelect={onInspectorSelect}
          isSelected={isInspectorSelected ?? false}
          selectButtonText={selectButtonText}
        />
      )}
    </Box>
  );
}

export default UniversalLocationMap;
